import { inject, Injectable } from '@angular/core';
import type { Params } from '@angular/router';
import { NavigationEnd, Router } from '@angular/router';

import type { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay } from 'rxjs';

import type { Language } from '@valk-nx/core/lib/core';
import { LANGUAGE } from '@valk-nx/core/lib/core';
import { StoryblokHelper } from '@valk-nx/storyblok-helpers/src/lib/general/storyblok.helper';
import type { RootInterface } from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

// A navigation that only changes the fragment (e.g. #map) shows the same story
const isSameStory = (previous: RootInterface, next: RootInterface) =>
	previous.slug === next.slug &&
	previous.language === next.language &&
	previous.isDraft === next.isDraft;

@Injectable({
	providedIn: 'root',
})
export class StoryblokParamsService {
	protected language = inject<Language>(LANGUAGE);

	public readonly storyblokParams$: Observable<RootInterface>;
	private readonly storyblokQueryParams$: Observable<Params>;
	private readonly router = inject(Router);

	constructor() {
		const navigationEnd$ = this.router.events.pipe(
			filter((event) => event instanceof NavigationEnd),
			shareReplay(1),
		);

		this.storyblokParams$ = navigationEnd$.pipe(
			// NOTE: We use the first child of root to get the activated route, as for StoryBlok, this is always the right one
			map(() => this.router.routerState.root.firstChild),
			filter((activatedRoute) => !!activatedRoute),
			map((activatedRoute) =>
				StoryblokHelper.getStoryblokParameters(
					activatedRoute.snapshot.url,
					activatedRoute.snapshot.queryParams,
					this.language,
				),
			),
			distinctUntilChanged(isSameStory),
			shareReplay(1),
		);

		this.storyblokQueryParams$ = navigationEnd$.pipe(
			// NOTE: We use the first child of root to get the activated route, as for StoryBlok, this is always the right one
			map(() => this.router.routerState.root.firstChild),
			filter((activatedRoute) => !!activatedRoute),
			map((activatedRoute) => activatedRoute.snapshot.queryParams),
			shareReplay(1),
		);
	}

	getFromUrlParam(paramName: string): Observable<string> {
		return this.storyblokQueryParams$.pipe(
			filter((params) => params[paramName]),
			map((params) => params[paramName]),
		);
	}
}
